import React from 'react';
import MaterialPaper from '@mui/material/Paper';
import type {PaperProps as MaterialPaperProps} from '@mui/material/Paper';
import {styled} from '@mui/material/styles';
import {FuelSize} from '../types/general';
import {excludeComponentProps} from '../utils/excludeComponentProps';

declare module '@mui/material/Paper' {
    interface PaperPropsVariantOverrides {
        elevation: false;
        elevated: true;
        outlined: true;
    }
}

type FuelBorderRadius = 'none' | FuelSize | 'xl' | '2xl' | 'full';

export interface PaperProps extends MaterialPaperProps {
    /**
     * Border Radius
     * @default 'lg'
     */
    borderRadius?: FuelBorderRadius;
}

const StyledPaper = styled(MaterialPaper, {
    ...excludeComponentProps('borderRadius')
})<PaperProps>(({
    theme, square, borderRadius, variant
}) => ({
    boxShadow: theme.tokens.FuelShadowBase,

    ...variant === 'elevated' && {
        boxShadow: theme.tokens.FuelShadowBase
    },

    ...variant === 'outlined' && {
        boxShadow: undefined,
        border: `solid 1px ${theme.tokens.FuelColorBorderLight}`
    },

    ...(borderRadius === 'sm' || borderRadius === 'small') && {
        borderRadius: theme.tokens.FuelBorderRadiusSm
    },
    ...(borderRadius === 'md' || borderRadius === 'medium') && {
        borderRadius: theme.tokens.FuelBorderRadiusMd
    },
    ...(borderRadius === 'lg' || borderRadius === 'large') && {
        borderRadius: theme.tokens.FuelBorderRadiusLg
    },
    ...borderRadius === 'xl' && {
        borderRadius: theme.tokens.FuelBorderRadiusXl
    },
    ...borderRadius === '2xl' && {
        borderRadius: theme.tokens.FuelBorderRadius2xl
    },
    ...borderRadius === 'full' && {
        borderRadius: theme.tokens.FuelBorderRadiusFull
    },
    ...(borderRadius === 'none' || square) && {
        borderRadius: theme.tokens.FuelBorderRadiusNone
    }
}));

export const Paper = React.forwardRef<any, PaperProps>(
    (
        {
            children,
            borderRadius = 'lg',
            ...props
        },
        ref
    ) => {
        return (
            <StyledPaper
                borderRadius={borderRadius}
                ref={ref}
                {...props}
            >
                {children}
            </StyledPaper>
        );
    });

Paper.displayName = 'Paper';
